import React, {
  useState, useEffect, useCallback, ComponentType,
} from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { Route, RouteProps } from 'react-router-dom';
import { deepLink } from 'Utils/deepLink';
import { isMobileDesign } from 'Utils/mobile';
import { GlobalLoadingCaller } from 'Utils/GlobalLoading';
import useIsEmailVerified from 'Utils/hooks/useIsEmailVerified';
import useProtectedApplication, { type ProtectedApplication } from 'Hooks/useProtectedApplication';
import { useConfig } from 'Contexts/Config';
import Urls, { PageStatus } from './Urls';

const isLoggedIn = () => Boolean(localStorage.email && localStorage.kueski_session);

function redirect(url: string) {
  window.location.assign(url);
}

interface PrivateRouteProps extends RouteProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  path: string;
  status?: ProtectedApplication['status'];
}

export default function PrivateRoute({
  status,
  component: Comp,
  ...routeProps
}: PrivateRouteProps) {
  const dispatch = useDispatch();
  const pushConnected = useCallback((...args: Parameters<typeof push>) => {
    dispatch(push(...args));
  }, [dispatch]);

  const [ready, setReady] = useState(false);
  const { features } = useConfig();
  const loggedIn = isLoggedIn();
  const isMobile = isMobileDesign();

  useEffect(() => {
    if (loggedIn) return;

    if (status === 'loan') {
      deepLink.set(`${window.location.pathname}${window.location.search}`);
    }
    redirect(Urls.landing);
  }, [loggedIn, status]);

  const { data: app } = useProtectedApplication({ enabled: loggedIn });
  useEffect(() => {
    if (!app) return;

    if (app.application_process_id !== null) {
      if (status && app.status !== status && status !== 'loan') {
        redirect(PageStatus[status]);
      }
      setReady(true);
      return;
    }

    if (status && status !== 'loan') {
      redirect(Urls.landing);
    } else {
      setReady(true);
    }
  }, [app, status]);

  const verificationEnabled = features.VERIFY_ACCOUNT && !isMobile && loggedIn;
  const { data: verification } = useIsEmailVerified({ enabled: verificationEnabled });
  useEffect(() => {
    if (!verification || !verificationEnabled) return;

    const isEmailVerified = verification.data.is_email_verified;
    const forceVerifyEmail = verification.data.force_verify_email;

    if (!isEmailVerified && forceVerifyEmail) {
      pushConnected(`${Urls.verifyAccount}/?send_email=true`);
    }
  }, [verificationEnabled, verification, pushConnected]);

  if (!ready || !app) return <GlobalLoadingCaller queryKey={['PrivateRoute']} />;
  return (
    <Route
      {...routeProps}
      render={params => <Comp {...params} app={app} />}
    />
  );
}

PrivateRoute.defaultProps = {
  status: '',
};
